/**
 * Slumpad men giltig testdata för formulären. Endast för admin – fyller bara i
 * fälten, skickar aldrig in något.
 */

import {
  GRADE_OPTIONS,
  afterschoolEligibility,
  padelEligibility,
  footballAcademyEligibility,
  type PeriodId,
  type TermId,
} from "./fees";
import { suggestPlacement } from "./placement";

const FIRST_GIRLS = ["Alva", "Ebba", "Maja", "Nora", "Signe", "Vera", "Lucia", "Elsa"];
const FIRST_BOYS = ["Elias", "Hugo", "Noel", "Vidar", "Milo", "Otto", "Nils", "Mateo"];
const ADULT_FIRST = ["Anna", "Karin", "Johan", "Peter", "Sofia", "Marta", "Erik", "Lena"];
const LAST = ["Lundgren", "Bergqvist", "Nyström", "Holm", "Sandberg", "Ek", "Falk", "Wik"];
const CITIES = [
  { city: "Estepona", postalCode: "29680", region: "Málaga" },
  { city: "Marbella", postalCode: "29602", region: "Málaga" },
  { city: "San Pedro de Alcántara", postalCode: "29670", region: "Málaga" },
];
const STREETS = ["Calle Real", "Avenida del Mar", "Calle Almendro", "Camino de Cortes"];

function pick<T>(list: readonly T[]): T {
  return list[Math.floor(Math.random() * list.length)]!;
}

function int(min: number, max: number) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function pad(n: number) {
  return String(n).padStart(2, "0");
}

/** Luhn-kontrollsiffra för svenskt personnummer. */
function luhnCheckDigit(nineDigits: string) {
  let sum = 0;
  for (let i = 0; i < nineDigits.length; i++) {
    let value = Number(nineDigits[i]) * (i % 2 === 0 ? 2 : 1);
    if (value > 9) value -= 9;
    sum += value;
  }
  return (10 - (sum % 10)) % 10;
}

/** Giltigt svenskt personnummer (ÅÅÅÅMMDD-NNNC) för ett givet födelsedatum. */
export function randomSwedishPersonalNumber(year: number, month: number, day: number) {
  const short = `${String(year).slice(2)}${pad(month)}${pad(day)}`;
  const serial = String(int(100, 999));
  const check = luhnCheckDigit(`${short}${serial}`);
  return `${year}${pad(month)}${pad(day)}-${serial}${check}`;
}

const NIE_LETTERS = "TRWAGMYFPDXBNJZSQVHLCKE";

/** Giltigt spanskt NIE med korrekt kontrollbokstav. */
export function randomNie() {
  const prefix = pick(["X", "Y", "Z"]);
  const digits = String(int(1000000, 9999999));
  const numeric = Number(`${"XYZ".indexOf(prefix)}${digits}`);
  return `${prefix}${digits}${NIE_LETTERS[numeric % 23]}`;
}

/** Giltigt spanskt DNI med korrekt kontrollbokstav. */
export function randomDni() {
  const digits = int(10000000, 99999999);
  return `${digits}${NIE_LETTERS[digits % 23]}`;
}

export type TestStudent = {
  studentFirstName: string;
  studentLastName: string;
  nationality: string;
  idNumber: string;
  birthYear: string;
  birthMonth: string;
  birthDay: string;
  gender: string;
  country: string;
  address: string;
  postalCode: string;
  city: string;
  region: string;
  /** Värde ur GRADE_OPTIONS, t.ex. "forskoleklass" eller "ak4". */
  grade: string;
  idCountry: string;
  previousSchool: string;
  notes: string;
  startTerm: TermId;
  period: PeriodId;
  wantsAfterschool: boolean;
  wantsFootballAcademy: boolean;
  wantsPadelTennis: boolean;
};

export type TestGuardian = {
  firstName: string;
  lastName: string;
  relation: string;
  email: string;
  phone: string;
  nationality: string;
  idNumber: string;
  country: string;
  address: string;
  postalCode: string;
  city: string;
  region: string;
  sameAddressAs: string;
  passportCountry: string;
};

const PREVIOUS_SCHOOLS = [
  "Vasaskolan, Malmö",
  "Sofiedalskolan, Göteborg",
  "Colegio San José, Estepona",
  "Ingen tidigare skola",
];

const NOTES = [
  "Testdata skapad med admin-knappen. Ingen riktig ansökan.",
  "Testdata: syskon önskar samma klass om möjligt.",
  "Testdata: barnet läser svenska hemma.",
];

/** Terminen testdatan utgår från – första terminen med fastställda avgifter. */
const TEST_TERM: TermId = "HT26";

/**
 * Slumpar en familj med angivet antal barn.
 * `nationality`: "Sverige" ger personnummer, "Spanien" ger NIE/DNI.
 */
export function randomFamily(
  children: 1 | 2 | 3,
  nationality: "Sverige" | "Spanien" = "Sverige",
  extraGuardian = false,
): { students: TestStudent[]; guardians: TestGuardian[] } {
  const family = pick(LAST);
  const place = pick(CITIES);
  const address = `${pick(STREETS)} ${int(1, 80)}`;
  const now = new Date();

  const students: TestStudent[] = [];
  const usedYears = new Set<number>();
  // Läsårets startår styr placeringen: 6 år = förskoleklass, 7 år = åk 1 …
  const termStartYear = 2000 + Number(TEST_TERM.slice(2));
  for (let i = 0; i < children; i++) {
    // Åldrar som alltid ger en giltig nivå i GRADE_OPTIONS (förskola–gymnasiet).
    let ageInAutumn = int(4, 17);
    while (usedYears.has(termStartYear - ageInAutumn)) ageInAutumn = int(4, 17);
    const year = termStartYear - ageInAutumn;
    usedYears.add(year);
    const girl = Math.random() < 0.5;
    const month = int(1, 12);
    const day = int(1, 28);
    const placement = suggestPlacement(
      `${year}-${pad(month)}-${pad(day)}`,
      TEST_TERM,
    );
    const grade = placement.grade ?? "";
    const program = GRADE_OPTIONS.find((g) => g.value === grade)?.program ?? null;
    const period: PeriodId = "term";
    students.push({
      studentFirstName: pick(girl ? FIRST_GIRLS : FIRST_BOYS),
      studentLastName: family,
      nationality,
      idNumber:
        nationality === "Sverige" ? randomSwedishPersonalNumber(year, month, day) : randomNie(),
      birthYear: String(year),
      birthMonth: String(month),
      birthDay: String(day),
      gender: girl ? "Flicka" : "Pojke",
      country: "Spanien",
      address,
      postalCode: place.postalCode,
      city: place.city,
      region: place.region,
      grade,
      idCountry: nationality,
      previousSchool: pick(PREVIOUS_SCHOOLS),
      notes: pick(NOTES),
      startTerm: TEST_TERM,
      period,
      wantsAfterschool: afterschoolEligibility(program, period).allowed,
      wantsFootballAcademy: footballAcademyEligibility(program, period, grade).allowed,
      wantsPadelTennis: padelEligibility(program, period).allowed && Math.random() < 0.5,
    });
  }

  function adult(relation: string): TestGuardian {
    const first = pick(ADULT_FIRST);
    const birthYear = now.getFullYear() - int(32, 48);
    return {
      firstName: first,
      lastName: family,
      relation,
      email: `${first.toLowerCase()}.${family.toLowerCase()}@beckman.club`.replace(/[^a-z.@]/g, ""),
      phone: `+4670${int(1000000, 9999999)}`,
      nationality,
      idNumber:
        nationality === "Sverige"
          ? randomSwedishPersonalNumber(birthYear, int(1, 12), int(1, 28))
          : randomDni(),
      country: "Spanien",
      address,
      postalCode: place.postalCode,
      city: place.city,
      region: place.region,
      sameAddressAs: "0",
      passportCountry: nationality,
    };
  }

  const guardians = [adult("Mamma")];
  if (extraGuardian) guardians.push(adult("Pappa"));
  return { students, guardians };
}

/** Testdata för kontakt-, besöks- och kursformulär. */
export function randomContactPerson() {
  const first = pick(ADULT_FIRST);
  const last = pick(LAST);
  return {
    name: `${first} ${last}`,
    email: `${first.toLowerCase()}.${last.toLowerCase()}@beckman.club`.replace(/[^a-z.@]/g, ""),
    phone: `+4670${int(1000000, 9999999)}`,
    message: "Testmeddelande skapat med admin-knappen för testdata.",
  };
}
